const safeObject = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
)

const clonePlain = (value, fallback = null) => {
    try {
        return JSON.parse(JSON.stringify(value))
    } catch (_error) {
        return fallback
    }
}

export const jsonByteLength = (value) => {
    try {
        const json = JSON.stringify(value ?? null)
        if (typeof TextEncoder !== 'undefined') {
            return new TextEncoder().encode(json).length
        }
        if (typeof Blob !== 'undefined') {
            return new Blob([json]).size
        }
        return json.length
    } catch (_error) {
        return 0
    }
}

const normalizeProductReference = (value) => {
    const raw = String(value || '').trim()
    if (!raw) return ''
    return raw.includes('__') ? raw.split('__')[0] : raw
}

const hasUsefulObjectValue = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false

    return Object.keys(value).some((key) => {
        const item = value[key]

        if (item === null || item === undefined || item === false) return false
        if (Array.isArray(item)) return item.length > 0
        if (typeof item === 'object') return Object.keys(item).length > 0

        return true
    })
}

export const hasSceneSnapshotData = (snapshot = {}) => {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return false

    return !!(
        snapshot.selectedModel ||
        (Array.isArray(snapshot.selectedModelPaths) && snapshot.selectedModelPaths.length) ||
        (Array.isArray(snapshot.modelPaths) && snapshot.modelPaths.length) ||
        hasUsefulObjectValue(snapshot.selectedModels) ||
        hasUsefulObjectValue(snapshot.instanceIds) ||
        hasUsefulObjectValue(snapshot.copyPaths) ||
        hasUsefulObjectValue(snapshot.positions) ||
        hasUsefulObjectValue(snapshot.rotations) ||
        hasUsefulObjectValue(snapshot.scales) ||
        hasUsefulObjectValue(snapshot.pivotPoints) ||
        hasUsefulObjectValue(snapshot.pivotPresetById) ||
        hasUsefulObjectValue(snapshot.customObjects) ||
        hasUsefulObjectValue(snapshot.colors) ||
        hasUsefulObjectValue(snapshot.dimensions) ||
        hasUsefulObjectValue(snapshot.hiddenModels) ||
        hasUsefulObjectValue(snapshot.solidObjects) ||
        hasUsefulObjectValue(snapshot.monumentAssemblies) ||
        hasUsefulObjectValue(snapshot.monumentDecorations) ||
        hasUsefulObjectValue(snapshot.monumentDecorationAssets) ||
        hasUsefulObjectValue(snapshot.modelPartsMeta) ||
        hasUsefulObjectValue(snapshot.productsMetaById) ||
        hasUsefulObjectValue(snapshot.modifierPreviews) ||
        hasUsefulObjectValue(snapshot.selectionGroups) ||
        hasUsefulObjectValue(snapshot.cameraState) ||
        hasUsefulObjectValue(snapshot.sceneScreenshots) ||
        hasUsefulObjectValue(snapshot.measure) ||
        hasUsefulObjectValue(snapshot.measures)
    )
}

export const compactProductsMetaById = (scene = {}, extraReferences = null) => {
    const source = safeObject(scene.productsMetaById)
    const entries = Object.entries(source).filter(([, meta]) => (
        meta && typeof meta === 'object' && !Array.isArray(meta)
    ))

    if (!entries.length) {
        return {
            productsMetaById: {},
            total: 0,
            kept: 0,
            beforeBytes: 2,
            afterBytes: 2,
        }
    }

    const aliasToKey = new Map()
    const modelPathToKey = new Map()

    entries.forEach(([key, meta]) => {
        ;[key, meta?._id, meta?.id, meta?.productId].forEach((candidate) => {
            const normalized = normalizeProductReference(candidate)
            if (normalized) aliasToKey.set(normalized, key)
        })

        const modelPath = String(meta?.modelPath || '').trim()
        if (modelPath) modelPathToKey.set(modelPath, key)
    })

    const referencedKeys = new Set()
    const seen = new WeakSet()
    let visited = 0

    const considerString = (value) => {
        const raw = String(value || '').trim()
        if (!raw) return

        const normalized = normalizeProductReference(raw)
        const aliasKey = aliasToKey.get(normalized)
        if (aliasKey) referencedKeys.add(aliasKey)

        const pathKey = modelPathToKey.get(raw)
        if (pathKey) referencedKeys.add(pathKey)
    }

    const visit = (value, depth = 0) => {
        if (
            depth > 14 ||
            visited > 150000 ||
            value === null ||
            value === undefined
        ) {
            return
        }

        visited += 1

        if (typeof value === 'string' || typeof value === 'number') {
            considerString(value)
            return
        }

        if (typeof value !== 'object') return
        if (seen.has(value)) return
        seen.add(value)

        if (Array.isArray(value)) {
            value.forEach((item) => visit(item, depth + 1))
            return
        }

        Object.entries(value).forEach(([key, item]) => {
            if (key === 'productsMetaById' || key === 'history') return
            considerString(key)
            visit(item, depth + 1)
        })
    }

    visit(scene)
    if (extraReferences) visit(extraReferences)

    const productsMetaById = {}

    if (referencedKeys.size) {
        referencedKeys.forEach((key) => {
            if (source[key]) productsMetaById[key] = source[key]
        })
    } else {
        // Для старой или нестандартной сцены лучше оставить metadata целиком,
        // чем случайно потерять историческую цену/размер.
        entries.forEach(([key, meta]) => {
            productsMetaById[key] = meta
        })
    }

    return {
        productsMetaById,
        total: entries.length,
        kept: Object.keys(productsMetaById).length,
        beforeBytes: jsonByteLength(source),
        afterBytes: jsonByteLength(productsMetaById),
    }
}

export const buildSceneCoreSnapshot = (
    sceneState = {},
    {
        viewCheckpoint = null,
        measure = null,
        compactProducts = true,
    } = {}
) => {
    const core = {
        selectedModel: sceneState.selectedModel || '',
        selectedModels: safeObject(sceneState.selectedModels),
        instanceIds: safeObject(sceneState.instanceIds),
        copyPaths: safeObject(sceneState.copyPaths),
        modelPaths: Array.isArray(sceneState.modelPaths) ? sceneState.modelPaths : [],
        selectedModelPaths: Array.isArray(sceneState.selectedModelPaths)
            ? sceneState.selectedModelPaths
            : [],
        positions: safeObject(sceneState.positions),
        rotations: safeObject(sceneState.rotations),
        scales: safeObject(sceneState.scales),
        pivotPoints: safeObject(sceneState.pivotPoints),
        pivotPresetById: safeObject(sceneState.pivotPresetById),
        colors: safeObject(sceneState.colors),
        dimensions: safeObject(sceneState.dimensions),
        customObjects: safeObject(sceneState.customObjects),
        hiddenModels: safeObject(sceneState.hiddenModels),
        solidObjects: safeObject(sceneState.solidObjects),
        productsMetaById: safeObject(sceneState.productsMetaById),
        selectionGroups: safeObject(sceneState.selectionGroups),
        activeSelectionGroupId: sceneState.activeSelectionGroupId || null,
        monumentPolished: !!sceneState.monumentPolished,
        modifierPreviews: safeObject(sceneState.modifierPreviews),

        monumentAssemblies: safeObject(sceneState.monumentAssemblies),
        monumentDecorations: safeObject(sceneState.monumentDecorations),
        monumentDecorationAssets: safeObject(sceneState.monumentDecorationAssets),
        modelPartsMeta: safeObject(sceneState.modelPartsMeta),

        cameraState: safeObject(sceneState.cameraState),
        showTextDimensions: sceneState.showTextDimensions !== false,
        lineColor: sceneState.lineColor,
        lineThickness: sceneState.lineThickness,
        fontSize: sceneState.fontSize,
        sceneColor: sceneState.sceneColor || null,
        gridColor: sceneState.gridColor || null,
        gridVisible: sceneState.gridVisible,
        minZ: sceneState.minZ,
        floorVisible: sceneState.floorVisible,
        floorMode: sceneState.floorMode,
        floorColor: sceneState.floorColor,
        floorRepeat: sceneState.floorRepeat,
        floorSize: sceneState.floorSize,
        floorText: sceneState.floorText,
        sunAngle: sceneState.sunAngle,
        sunVertical: sceneState.sunVertical,
        sunIntensity: sceneState.sunIntensity,
        shadowsEnabled: sceneState.shadowsEnabled,
        graphicsQuality: sceneState.graphicsQuality,

        sceneScreenshots: safeObject(sceneState.sceneScreenshots),
        measure: safeObject(measure || sceneState.measure),
    }

    if (viewCheckpoint) {
        core.viewCheckpoint = clonePlain(viewCheckpoint, null)
    }

    const compactMeta = compactProducts
        ? compactProductsMetaById(core, viewCheckpoint)
        : {
            productsMetaById: core.productsMetaById,
            total: Object.keys(core.productsMetaById).length,
            kept: Object.keys(core.productsMetaById).length,
            beforeBytes: jsonByteLength(core.productsMetaById),
            afterBytes: jsonByteLength(core.productsMetaById),
        }

    core.productsMetaById = compactMeta.productsMetaById

    return {
        snapshot: core,
        productsMeta: compactMeta,
    }
}

export const buildCanonicalSceneSnapshot = (state = {}, extra = {}) => {
    const scene = state.scene || {}
    const office = state.office || {}
    const measure = state.measure || scene.measure || {}
    const calc3dUi = state.calc3dUi || {}
    const { snapshot: core, productsMeta } = buildSceneCoreSnapshot(scene, {
        viewCheckpoint: extra.viewCheckpoint || scene.viewCheckpoint || null,
        measure,
        compactProducts: true,
    })

    const snapshot = {
        version: 3,
        savedAt: new Date().toISOString(),
        accessRules: {
            view: 'permanent',
            editDays: Number(extra.editDays || 30),
        },
        officeId: extra.officeId || office.currentOfficeId || null,
        sceneId: extra.sceneId || null,
        salesSessionId: extra.salesSessionId || null,
        sessionId: extra.salesSessionId || null,
        title: extra.title || 'Разовая 3D-сцена',
        ...core,
        measures: core.measure,
        ui: calc3dUi,
    }

    return {
        snapshot,
        productsMeta,
    }
}
